import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API_URL } from '../config/api';
import DeadEmailCleanup from '../components/DeadEmailCleanup';
import {
  Plus, MagnifyingGlass, Buildings, Phone, EnvelopeSimple,
  ArrowRight, Funnel, CircleNotch, Fire, Snowflake, SunDim,
  UserPlus, TrendUp, CalendarCheck, ChartPie, Upload, Bell,
  FileText, User, WarningCircle, Envelope, ChartBar
} from '@phosphor-icons/react';


const STATUS_LABELS = {
  new: { label: 'Nuevo', color: 'bg-slate-100 text-slate-700' },
  contacted: { label: 'Contactado', color: 'bg-blue-100 text-blue-700' },
  interested: { label: 'Interesado', color: 'bg-amber-100 text-amber-700' },
  qualified: { label: 'Calificado', color: 'bg-violet-100 text-violet-700' },
  converted: { label: 'Convertido', color: 'bg-green-100 text-green-700' },
  lost: { label: 'Perdido', color: 'bg-red-100 text-red-700' },
};

const TEMP_ICONS = {
  hot: { icon: Fire, color: 'text-red-500', label: 'Hot' },
  warm: { icon: SunDim, color: 'text-amber-500', label: 'Warm' },
  cold: { icon: Snowflake, color: 'text-blue-400', label: 'Cold' },
};

export default function ProspectsPage() {
  const navigate = useNavigate();
  const [prospects, setProspects] = useState([]);
  const [stats, setStats] = useState(null);
  const [reminders, setReminders] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterTemp, setFilterTemp] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [showCsvImport, setShowCsvImport] = useState(false);
  const [csvData, setCsvData] = useState(null);
  const [csvImporting, setCsvImporting] = useState(false);
  const fileInputRef = useRef(null);
  const [form, setForm] = useState({
    company_name: '', industry: '', contact_name: '', contact_title: '',
    contact_email: '', contact_phone: '', website: '', address: '', city: '',
    state: 'FL', employee_count: 0, services_needed: [], notes: '', source: 'manual'
  });

  const fetchProspects = useCallback(async () => {
    try {
      let url = `${API_URL}/api/b2b/prospects?`;
      if (filterStatus) url += `status=${filterStatus}&`;
      if (filterTemp) url += `temperature=${filterTemp}&`;
      const res = await axios.get(url, { withCredentials: true });
      setProspects(res.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [filterStatus, filterTemp]);

  const fetchStats = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/b2b/stats`, { withCredentials: true });
      setStats(res.data);
    } catch (err) {
      console.error(err);
    }
  }, []);

  const fetchReminders = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/b2b/reminders`, { withCredentials: true });
      setReminders(res.data);
    } catch (err) {
      console.error(err);
    }
  }, []);

  useEffect(() => { fetchProspects(); fetchStats(); fetchReminders(); }, [fetchProspects, fetchStats, fetchReminders]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API_URL}/api/b2b/prospects`, form, { withCredentials: true });
      setShowForm(false);
      setForm({ company_name: '', industry: '', contact_name: '', contact_title: '', contact_email: '', contact_phone: '', website: '', address: '', city: '', state: 'FL', employee_count: 0, services_needed: [], notes: '', source: 'manual' });
      fetchProspects();
      fetchStats();
    } catch (err) {
      alert('Error al crear prospecto');
    }
  };

  const filtered = prospects.filter(p =>
    (p.company_name || '').toLowerCase().includes(search.toLowerCase()) ||
    (p.contact_name || '').toLowerCase().includes(search.toLowerCase()) ||
    (p.industry || '').toLowerCase().includes(search.toLowerCase())
  );

  const services = ['Cableado Estructurado', 'CCTV', 'Control de Acceso', 'AV Profesional', 'Redes PoE', 'Wi-Fi Empresarial', 'Fibra Optica', 'Alarmas'];

  const handleCsvFile = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (evt) => {
      const text = evt.target.result;
      const lines = text.split('\n').filter(l => l.trim());
      if (lines.length < 2) { alert('CSV vacio o sin datos'); return; }
      const headers = lines[0].split(',').map(h => h.trim().replace(/"/g, ''));
      const rows = lines.slice(1).map(line => {
        const values = line.split(',').map(v => v.trim().replace(/"/g, ''));
        const obj = {};
        headers.forEach((h, i) => { obj[h] = values[i] || ''; });
        return obj;
      });
      setCsvData({ headers, rows });
      setShowCsvImport(true);
    };
    reader.readAsText(file);
    e.target.value = '';
  };

  const handleCsvImport = async () => {
    if (!csvData) return;
    setCsvImporting(true);
    try {
      const res = await axios.post(`${API_URL}/api/b2b/prospects/import-csv`, {
        rows: csvData.rows,
        column_map: {
          company_name: csvData.headers.find(h => /company|empresa|name/i.test(h)) || 'company_name',
          contact_name: csvData.headers.find(h => /contact|contacto|person/i.test(h)) || 'contact_name',
          contact_email: csvData.headers.find(h => /email|correo/i.test(h)) || 'contact_email',
          contact_phone: csvData.headers.find(h => /phone|telefono|tel/i.test(h)) || 'contact_phone',
          industry: csvData.headers.find(h => /industry|industria|sector/i.test(h)) || 'industry',
          city: csvData.headers.find(h => /city|ciudad/i.test(h)) || 'city',
          website: csvData.headers.find(h => /website|web|url/i.test(h)) || 'website',
          notes: csvData.headers.find(h => /notes|notas|comments/i.test(h)) || 'notes',
        }
      }, { withCredentials: true });
      alert(`${res.data.imported} prospectos importados${res.data.errors?.length ? `\n${res.data.errors.length} errores` : ''}`);
      setShowCsvImport(false);
      setCsvData(null);
      fetchProspects();
      fetchStats();
    } catch (err) {
      alert('Error al importar');
    } finally {
      setCsvImporting(false);
    }
  };

  if (loading) return <div className="flex justify-center py-20"><CircleNotch size={40} className="animate-spin text-blue-600" /></div>;

  return (
    <div className="space-y-6 animate-fadeIn" data-testid="prospects-page">
      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-3" data-testid="b2b-stats">
          <div className="bg-white rounded-xl border border-slate-200 p-4">
            <div className="flex items-center gap-2 mb-1">
              <Buildings size={18} className="text-slate-500" />
              <span className="text-xs text-slate-500 font-medium">Total</span>
            </div>
            <p className="text-2xl font-bold text-slate-900">{stats.total}</p>
          </div>
          <div className="bg-white rounded-xl border border-slate-200 p-4">
            <div className="flex items-center gap-2 mb-1">
              <Fire size={18} className="text-red-500" />
              <span className="text-xs text-slate-500 font-medium">Hot</span>
            </div>
            <p className="text-2xl font-bold text-red-600">{stats.by_temperature?.hot || 0}</p>
          </div>
          <div className="bg-white rounded-xl border border-slate-200 p-4">
            <div className="flex items-center gap-2 mb-1">
              <CalendarCheck size={18} className="text-amber-500" />
              <span className="text-xs text-slate-500 font-medium">Seguimientos</span>
            </div>
            <p className="text-2xl font-bold text-amber-600">{stats.followups_due || 0}</p>
          </div>
          <div className="bg-white rounded-xl border border-slate-200 p-4">
            <div className="flex items-center gap-2 mb-1">
              <TrendUp size={18} className="text-green-500" />
              <span className="text-xs text-slate-500 font-medium">Conversion</span>
            </div>
            <p className="text-2xl font-bold text-green-600">{stats.conversion_rate || 0}%</p>
          </div>
          <div className="bg-white rounded-xl border border-slate-200 p-4">
            <div className="flex items-center gap-2 mb-1">
              <Phone size={18} className="text-blue-500" />
              <span className="text-xs text-slate-500 font-medium">Esta Semana</span>
            </div>
            <p className="text-2xl font-bold text-blue-600">{stats.contacted_this_week || 0}</p>
          </div>
        </div>
      )}

      {/* Dead Email Cleanup Banner */}
      <DeadEmailCleanup onCleaned={() => { fetchProspects(); fetchStats(); }} />

      {/* Reminders Banner */}
      {reminders && reminders.total_overdue > 0 && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-4 flex items-center gap-3" data-testid="reminders-banner">
          <WarningCircle size={22} className="text-red-500 shrink-0" weight="fill" />
          <div className="flex-1">
            <p className="text-sm font-semibold text-red-800">{reminders.total_overdue} seguimiento{reminders.total_overdue > 1 ? 's' : ''} vencido{reminders.total_overdue > 1 ? 's' : ''}</p>
            <div className="flex flex-wrap gap-2 mt-1">
              {reminders.overdue.slice(0, 3).map(p => (
                <Link key={p.id} to={`/prospects/${p.id}`} className="text-xs text-red-600 hover:underline">
                  {p.company_name}
                </Link>
              ))}
              {reminders.total_overdue > 3 && <span className="text-xs text-red-500">+{reminders.total_overdue - 3} mas</span>}
            </div>
          </div>
        </div>
      )}
      {reminders && reminders.total_upcoming > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 flex items-center gap-3" data-testid="upcoming-banner">
          <Bell size={18} className="text-amber-500 shrink-0" />
          <p className="text-sm text-amber-800">
            <span className="font-medium">{reminders.total_upcoming}</span> seguimiento{reminders.total_upcoming > 1 ? 's' : ''} en los proximos 3 dias
          </p>
        </div>
      )}

      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Prospectos B2B</h1>
          <p className="text-slate-500 text-sm">{filtered.length} prospectos</p>
        </div>
        <div className="flex gap-2">
          <button onClick={() => navigate('/campaigns')} className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-50 text-emerald-700 rounded-lg hover:bg-emerald-100 transition-colors text-sm" data-testid="campaigns-btn">
            <Envelope size={18} /> Campanas
          </button>
          <button onClick={() => navigate('/b2b-reports')} className="inline-flex items-center gap-2 px-4 py-2 bg-violet-50 text-violet-700 rounded-lg hover:bg-violet-100 transition-colors text-sm" data-testid="b2b-reports-btn">
            <ChartBar size={18} /> Reportes
          </button>
          <input type="file" ref={fileInputRef} accept=".csv" onChange={handleCsvFile} className="hidden" />
          <button onClick={() => fileInputRef.current?.click()} className="inline-flex items-center gap-2 px-4 py-2 bg-slate-100 text-slate-700 rounded-lg hover:bg-slate-200 transition-colors text-sm" data-testid="import-csv-btn">
            <Upload size={18} /> Importar CSV
          </button>
          <button
            onClick={async () => {
              if (!window.confirm('Enriquecer hasta 30 prospectos sin email valido scrapeando sus websites? Puede tardar 1-2 minutos.')) return;
              try {
                const res = await axios.post(`${API_URL}/api/b2b/prospects/enrich-bulk`, { only_missing_email: true }, { withCredentials: true });
                alert(`Enrichment terminado: ${res.data.successful} exitosos, ${res.data.failed} fallidos de ${res.data.processed}.`);
                window.location.reload();
              } catch (e) {
                alert('Error: ' + (e.response?.data?.detail || e.message));
              }
            }}
            className="inline-flex items-center gap-2 px-4 py-2 bg-amber-50 text-amber-700 rounded-lg hover:bg-amber-100 transition-colors text-sm"
            data-testid="enrich-bulk-btn"
            title="Scrapea las webs de los prospectos para encontrar emails y telefonos reales"
          >
            <MagnifyingGlass size={18} /> Enriquecer Contactos
          </button>
          <button onClick={() => setShowForm(true)} className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors" data-testid="new-prospect-btn">
            <Plus size={18} weight="bold" /> Nuevo Prospecto
          </button>
        </div>
      </div>

      {/* CSV Import Preview */}
      {showCsvImport && csvData && (
        <div className="bg-white rounded-xl border border-slate-200 p-5" data-testid="csv-preview">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-semibold text-slate-900">Vista Previa CSV</h3>
            <span className="text-sm text-slate-500">{csvData.rows.length} filas</span>
          </div>
          <div className="overflow-x-auto max-h-[200px] border border-slate-200 rounded-lg mb-3">
            <table className="w-full text-xs">
              <thead className="bg-slate-50 sticky top-0">
                <tr>{csvData.headers.map(h => <th key={h} className="px-3 py-2 text-left font-medium text-slate-600">{h}</th>)}</tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {csvData.rows.slice(0, 5).map((row, i) => (
                  <tr key={i}>{csvData.headers.map(h => <td key={h} className="px-3 py-1.5 text-slate-700">{row[h]}</td>)}</tr>
                ))}
              </tbody>
            </table>
          </div>
          {csvData.rows.length > 5 && <p className="text-xs text-slate-400 mb-3">Mostrando 5 de {csvData.rows.length} filas</p>}
          <div className="flex gap-2 justify-end">
            <button onClick={() => { setShowCsvImport(false); setCsvData(null); }} className="px-4 py-2 bg-slate-100 text-slate-700 rounded-lg text-sm">Cancelar</button>
            <button onClick={handleCsvImport} disabled={csvImporting} className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm disabled:opacity-50" data-testid="confirm-csv-import">
              {csvImporting ? 'Importando...' : `Importar ${csvData.rows.length} Prospectos`}
            </button>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-wrap gap-3 items-center">
        <div className="relative flex-1 min-w-[200px] max-w-md">
          <MagnifyingGlass size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input type="text" value={search} onChange={e => setSearch(e.target.value)} placeholder="Buscar empresa, contacto..." className="w-full pl-10 pr-4 py-2 border border-slate-200 rounded-lg text-sm" data-testid="prospect-search" />
        </div>
        <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)} className="px-3 py-2 border border-slate-200 rounded-lg text-sm" data-testid="filter-status">
          <option value="">Todos los estados</option>
          {Object.entries(STATUS_LABELS).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
        </select>
        <select value={filterTemp} onChange={e => setFilterTemp(e.target.value)} className="px-3 py-2 border border-slate-200 rounded-lg text-sm" data-testid="filter-temp">
          <option value="">Temperatura</option>
          <option value="hot">Hot</option>
          <option value="warm">Warm</option>
          <option value="cold">Cold</option>
        </select>
      </div>

      {/* Prospects Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
        {filtered.map(p => {
          const st = STATUS_LABELS[p.status] || STATUS_LABELS.new;
          const temp = TEMP_ICONS[p.temperature] || TEMP_ICONS.cold;
          const TempIcon = temp.icon;
          return (
            <Link key={p.id} to={`/prospects/${p.id}`} className="bg-white rounded-xl border border-slate-200 p-5 hover:shadow-md hover:border-blue-200 transition-all group" data-testid={`prospect-card-${p.id}`}>
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center gap-2">
                  <div className="w-10 h-10 rounded-lg bg-slate-100 flex items-center justify-center">
                    <Buildings size={20} className="text-slate-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-slate-900 group-hover:text-blue-600 transition-colors text-sm">{p.company_name}</h3>
                    <p className="text-xs text-slate-500">{p.industry || 'Sin industria'}</p>
                  </div>
                </div>
                <TempIcon size={20} className={temp.color} weight="fill" />
              </div>
              {p.contact_name && (
                <p className="text-sm text-slate-700 mb-1 font-medium">{p.contact_name} {p.contact_title && <span className="text-slate-400 font-normal">- {p.contact_title}</span>}</p>
              )}
              <div className="flex flex-wrap gap-2 text-xs text-slate-500 mb-3">
                {p.contact_phone && <span className="flex items-center gap-1"><Phone size={12} /> {p.contact_phone}</span>}
                {p.contact_email && <span className="flex items-center gap-1"><EnvelopeSimple size={12} /> {p.contact_email}</span>}
              </div>
              <div className="flex items-center justify-between">
                <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${st.color}`}>{st.label}</span>
                <div className="flex items-center gap-2">
                  {p.assigned_to && (
                    <span className="flex items-center gap-1 text-xs text-violet-600 bg-violet-50 px-1.5 py-0.5 rounded">
                      <User size={10} /> {p.assigned_to.split(' ')[0]}
                    </span>
                  )}
                  <div className="flex items-center gap-1">
                    <div className="w-16 h-1.5 bg-slate-100 rounded-full overflow-hidden">
                      <div className="h-full rounded-full bg-blue-500 transition-all" style={{ width: `${p.score || 0}%` }} />
                    </div>
                    <span className="text-xs text-slate-400">{p.score || 0}</span>
                  </div>
                </div>
              </div>
            </Link>
          );
        })}
      </div>

      {filtered.length === 0 && !loading && (
        <div className="text-center py-16 text-slate-400">
          <Buildings size={48} className="mx-auto mb-3 opacity-50" />
          <p>No hay prospectos. Crea el primero.</p>
        </div>
      )}

      {/* New Prospect Modal */}
      {showForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="bg-white rounded-2xl w-full max-w-2xl max-h-[85vh] overflow-y-auto p-6" data-testid="new-prospect-modal">
            <h2 className="text-xl font-bold mb-4">Nuevo Prospecto</h2>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-slate-700">Empresa *</label>
                  <input required value={form.company_name} onChange={e => setForm({...form, company_name: e.target.value})} className="w-full mt-1 px-3 py-2 border border-slate-200 rounded-lg text-sm" data-testid="input-company" />
                </div>
                <div>
                  <label className="text-sm font-medium text-slate-700">Industria</label>
                  <select value={form.industry} onChange={e => setForm({...form, industry: e.target.value})} className="w-full mt-1 px-3 py-2 border border-slate-200 rounded-lg text-sm">
                    <option value="">Seleccionar</option>
                    <option value="Construccion">Construccion</option>
                    <option value="Hoteleria">Hoteleria</option>
                    <option value="Retail">Retail</option>
                    <option value="Oficinas">Oficinas</option>
                    <option value="Educacion">Educacion</option>
                    <option value="Salud">Salud</option>
                    <option value="Gobierno">Gobierno</option>
                    <option value="Industrial">Industrial</option>
                    <option value="Residencial">Residencial</option>
                    <option value="Financiero">Financiero</option>
                    <option value="Otro">Otro</option>
                  </select>
                </div>
                <div>
                  <label className="text-sm font-medium text-slate-700">Contacto</label>
                  <input value={form.contact_name} onChange={e => setForm({...form, contact_name: e.target.value})} className="w-full mt-1 px-3 py-2 border border-slate-200 rounded-lg text-sm" />
                </div>
                <div>
                  <label className="text-sm font-medium text-slate-700">Cargo</label>
                  <input value={form.contact_title} onChange={e => setForm({...form, contact_title: e.target.value})} className="w-full mt-1 px-3 py-2 border border-slate-200 rounded-lg text-sm" />
                </div>
                <div>
                  <label className="text-sm font-medium text-slate-700">Email</label>
                  <input type="email" value={form.contact_email} onChange={e => setForm({...form, contact_email: e.target.value})} className="w-full mt-1 px-3 py-2 border border-slate-200 rounded-lg text-sm" />
                </div>
                <div>
                  <label className="text-sm font-medium text-slate-700">Telefono</label>
                  <input value={form.contact_phone} onChange={e => setForm({...form, contact_phone: e.target.value})} className="w-full mt-1 px-3 py-2 border border-slate-200 rounded-lg text-sm" />
                </div>
                <div>
                  <label className="text-sm font-medium text-slate-700">Website</label>
                  <input value={form.website} onChange={e => setForm({...form, website: e.target.value})} className="w-full mt-1 px-3 py-2 border border-slate-200 rounded-lg text-sm" />
                </div>
                <div>
                  <label className="text-sm font-medium text-slate-700">Empleados</label>
                  <input type="number" value={form.employee_count} onChange={e => setForm({...form, employee_count: parseInt(e.target.value) || 0})} className="w-full mt-1 px-3 py-2 border border-slate-200 rounded-lg text-sm" />
                </div>
                <div className="md:col-span-2">
                  <label className="text-sm font-medium text-slate-700">Ciudad / Estado</label>
                  <div className="flex gap-2 mt-1">
                    <input value={form.city} onChange={e => setForm({...form, city: e.target.value})} placeholder="Ciudad" className="flex-1 px-3 py-2 border border-slate-200 rounded-lg text-sm" />
                    <input value={form.state} onChange={e => setForm({...form, state: e.target.value})} placeholder="FL" className="w-20 px-3 py-2 border border-slate-200 rounded-lg text-sm" />
                  </div>
                </div>
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700">Servicios Necesarios</label>
                <div className="flex flex-wrap gap-2 mt-1">
                  {services.map(s => (
                    <label key={s} className={`px-3 py-1.5 rounded-lg border text-xs font-medium cursor-pointer transition-colors ${form.services_needed.includes(s) ? 'bg-blue-50 border-blue-300 text-blue-700' : 'bg-white border-slate-200 text-slate-600 hover:bg-slate-50'}`}>
                      <input type="checkbox" className="hidden" checked={form.services_needed.includes(s)} onChange={() => {
                        const arr = form.services_needed.includes(s) ? form.services_needed.filter(x => x !== s) : [...form.services_needed, s];
                        setForm({...form, services_needed: arr});
                      }} />
                      {s}
                    </label>
                  ))}
                </div>
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700">Notas</label>
                <textarea value={form.notes} onChange={e => setForm({...form, notes: e.target.value})} rows={3} className="w-full mt-1 px-3 py-2 border border-slate-200 rounded-lg text-sm" />
              </div>
              <div className="flex gap-3 pt-2">
                <button type="submit" className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium" data-testid="submit-prospect-btn">Crear Prospecto</button>
                <button type="button" onClick={() => setShowForm(false)} className="px-6 py-2 bg-slate-100 text-slate-700 rounded-lg hover:bg-slate-200 transition-colors">Cancelar</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
